import React, { useState, useEffect } from "react";
import { Check, ArrowRight, CalendarDays, Radar, Bookmark, Bell, MessagesSquare } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";

const STEPS = [
  { key: "visited_today", label: "Open Today", desc: "See your daily investigation hub — Trade of the Day, Daily Brief, and top signals.", icon: CalendarDays, to: "/app/today" },
  { key: "inspected_signal", label: "Inspect a signal", desc: "Click into a signal to see which traders are driving it and why.", icon: Radar, to: "/app/signals" },
  { key: "created_watchlist", label: "Create a watchlist", desc: "Track markets and traders that matter to you.", icon: Bookmark, to: "/app/watchlists" },
  { key: "enabled_alert", label: "Enable an alert", desc: "Get notified when something changes on a market you care about.", icon: Bell, to: "/app/alerts" },
  { key: "asked_chat", label: "Ask Chat a question", desc: "Persistent intelligence chat — ask about markets, traders, or signals.", icon: MessagesSquare, to: "/app/chat" },
];

export default function FirstRunChecklist() {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/onboarding/progress").then(({ data }) => {
      setProgress(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading || !progress) return null;

  const completed = Object.entries(progress).filter(([, v]) => v).length;
  if (completed >= STEPS.length) return null;

  return (
    <div className="rounded-lg border border-zinc-800 bg-[#0b0b0f] p-5 mb-6" data-testid="first-run-checklist">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-zinc-100">Getting started</h3>
        <span className="text-xs text-zinc-500">{completed}/{STEPS.length} done</span>
      </div>
      <div className="w-full h-1.5 bg-zinc-800 rounded-full mb-4 overflow-hidden">
        <div
          className="h-full bg-zinc-100 rounded-full transition-all duration-500"
          style={{ width: `${(completed / STEPS.length) * 100}%` }}
        />
      </div>
      <div className="space-y-2">
        {STEPS.map((step) => {
          const done = !!progress?.[step.key];
          return (
            <Link
              key={step.key}
              to={step.to}
              className={`flex items-start gap-3 p-2 rounded-md transition-colors ${
                done ? "opacity-40" : "hover:bg-zinc-900"
              }`}
            >
              <div className={`mt-0.5 w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${
                done ? "border-emerald-600 bg-emerald-950/30" : "border-zinc-700"
              }`}>
                {done ? (
                  <Check size={11} className="text-emerald-400" />
                ) : (
                  <span className="text-[10px] text-zinc-500">{STEPS.indexOf(step) + 1}</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-medium ${done ? "text-zinc-600" : "text-zinc-100"}`}>
                  {step.label}
                </div>
                <div className="text-xs text-zinc-500 mt-0.5">{step.desc}</div>
              </div>
              {!done && <ArrowRight size={13} className="text-zinc-600 mt-1 shrink-0" />}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
